"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Menu, ShoppingCart, Sparkles } from "lucide-react";
import { Drawer } from "../common/Drawer";
import { LanguageSelector } from "./LanguageSelector";
import Image from "next/image";
import { getAllActiveSettings } from "@/modules/home/home.service";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setCartDrawerOpen } from "@/redux/slices/cartSlice";

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dispatch = useAppDispatch();
  const cartItems = useAppSelector((state) => state.cart.items);
  const { isCustomerAuthenticated, user } = useAppSelector(
    (state) => state.auth,
  );

  const { SettingList } = useAppSelector((state) => state.home);

  React.useMemo(() => {
    if (SettingList.length === 0) {
      dispatch(getAllActiveSettings());
    }
  }, []);
  // Hydration is now handled centrally by HydrationWrapper in ReduxProvider

  const cartCount = cartItems.reduce(
    (total, item) => total + (Number(item.Qty) || 0),
    0,
  );

  const handleCartClick = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(setCartDrawerOpen(true));
  };

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "Shop", href: "/shop" },
    { name: "Cost To Cost", href: "/cost-to-cost" },
    { name: "Other Products", href: "/other-products" },
    { name: "About", href: "/about" },
    { name: "Wholesale", href: "/wholesale" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <>
      <nav className="sticky top-0 z-50 h-[76px] bg-black/95 border-b border-border-1 backdrop-blur-xl px-4 md:px-14 flex items-center justify-between shadow-sm">
        <Link href="/" className="flex items-center">
          <Image
            src="/images/logo.jpeg"
            alt="NakMan Jewellery"
            width={160}
            height={60}
            className="h-[60px] w-auto object-contain mix-blend-multiply"
            priority
          />
        </Link>

        {/* Desktop Navigation */}
        <ul className="hidden xl:flex gap-6 list-none items-center">
          {navLinks.map((link) => {
            const isShop = link.name.toLowerCase() === "shop";
            return (
              <li key={link.name}>
                <Link
                  href={link.href}
                  className={
                    isShop
                      ? "px-4 py-1.5 bg-[#5c2020] text-white text-[12px] font-bold uppercase tracking-[1.1px] rounded-full shadow-md hover:bg-[#7a2a2a] hover:shadow-lg transition-all flex items-center gap-1.5 group hover:scale-105 ring-2 ring-[#5c2020]/20"
                      : "text-[13px] text-text-1 uppercase tracking-[0.9px] font-medium transition-colors hover:text-gold-core whitespace-nowrap"
                  }
                >
                  {isShop && (
                    <Sparkles
                      size={13}
                      className="text-amber-300 fill-amber-300 animate-pulse"
                    />
                  )}
                  {link.name}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Mid-sized and Mobile UI */}
        <div className="flex items-center gap-3 sm:gap-4.5">
          <Link
            href={isCustomerAuthenticated ? "/my-account" : "/login"}
            className="hidden sm:block border border-gold-core text-gold-core px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] transition-all hover:bg-gold-core hover:text-white rounded-sm"
          >
            {isCustomerAuthenticated ? user?.name || "Account" : "Login"}
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageSelector />

            <button
              onClick={handleCartClick}
              className="relative text-text-1 cursor-pointer transition-colors hover:text-gold-core p-2"
            >
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 bg-gold-core text-white text-[8px] font-extrabold w-4 h-4 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
          <button
            className={`${isMobileMenuOpen ? "opacity-0 pointer-events-none" : "xl:hidden"} text-text-1 p-2 focus:outline-none transition-opacity cursor-pointer`}
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Toggle menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </nav>

      <Drawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        title={
          <Image
            src="/images/logo.jpeg"
            alt="NakMan"
            width={120}
            height={40}
            className="h-[40px] w-auto object-contain mix-blend-multiply"
          />
        }
        position="right"
        variant="light"
      >
        <div className="flex flex-col h-full justify-between pb-4">
          <div>
            <span className="text-[10px] text-text-3 uppercase tracking-widest block mb-4 opacity-70">
              Menu Navigation
            </span>
            <ul className="flex flex-col gap-0 list-none">
              {navLinks.map((link) => {
                const isShop = link.name.toLowerCase() === "shop";
                return (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className={
                        isShop
                          ? "text-lg font-cormorant font-bold text-white bg-[#5c2020] px-4 py-3 rounded-xl uppercase tracking-widest flex items-center justify-between my-2 shadow-md hover:bg-[#7a2a2a] transition-all"
                          : "text-lg font-cormorant font-bold text-text-1 uppercase tracking-widest block py-4 border-b border-border-1 hover:text-gold-core transition-colors"
                      }
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="flex items-center gap-2">
                        {isShop && (
                          <Sparkles
                            size={16}
                            className="text-amber-300 fill-amber-300"
                          />
                        )}
                        {link.name}
                      </span>
                      {isShop && (
                        <span className="text-[10px] bg-amber-400 text-black px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider font-sans">
                          Explore
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="flex flex-col gap-4">
            <Link
              href={isCustomerAuthenticated ? "/my-account" : "/login"}
              className="mt-4 block border border-gold-dim text-gold-core px-6 py-4 text-center text-[12px] font-bold uppercase tracking-[0.14em] transition-all bg-gold-core hover:bg-white text-white hover:text-gold-core"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {isCustomerAuthenticated
                ? user?.name || "My Account"
                : "Login / Register"}
            </Link>

            {/* <Image
              src="/images/logo.jpeg"
              alt="Malabar Enterprise"
              width={100}
              height={30}
              className="h-[30px] w-auto object-contain mx-auto opacity-60 mix-blend-multiply"
            /> */}
          </div>
        </div>
      </Drawer>
    </>
  );
};
